import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import FreelancerCard, { FreelancerCardSkeleton } from '../components/FreelancerCard';
import AdBanner from '../components/AdBanner';
import { Button } from '../components/ui/button';
import { Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { sanitizeHtml } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_ITEMS_PER_PAGE = 24;

const MOBILE_CLASSES = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
const DESKTOP_CLASSES = {
  4: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  5: 'sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
  6: 'sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
};
const buildGridClass = (mobile, desktop) =>
  `grid ${MOBILE_CLASSES[mobile] || 'grid-cols-3'} ${DESKTOP_CLASSES[desktop] || DESKTOP_CLASSES[6]} gap-2 sm:gap-4`;

const HomePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homepageSections, setHomepageSections] = useState([]);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchListings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSiteSettings(response.data);
      setHomepageSections(response.data.homepage_sections || []);
      const ipp = Number(response.data.items_per_page) || DEFAULT_ITEMS_PER_PAGE;
      setVisibleCount(ipp);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/listings`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // Compute sidebar visibility and grid columns from settings
  const computeLayout = () => {
    const mobileCols = Number(siteSettings?.mobile_grid_columns) || 3;
    if (!siteSettings) return { showSidebar: false, gridClass: buildGridClass(mobileCols, 6) };

    const adPages = siteSettings.ad_pages || {};
    const pageEnabled = adPages.home !== false;
    const ads = siteSettings.ads || [];
    const leftAd = ads.find(a => a.position === 'left');
    const rightAd = ads.find(a => a.position === 'right');
    const hasActiveSidebar = pageEnabled && (
      (leftAd && leftAd.active === true) || (rightAd && rightAd.active === true)
    );

    if (hasActiveSidebar) {
      return { showSidebar: true, gridClass: buildGridClass(mobileCols, 4), leftAd, rightAd, pageEnabled };
    }

    const cols = Number(siteSettings.grid_columns) || 6;
    return { showSidebar: false, gridClass: buildGridClass(mobileCols, cols), leftAd, rightAd, pageEnabled };
  };

  const { showSidebar, gridClass, leftAd, rightAd, pageEnabled } = computeLayout();

  // Load more
  const itemsPerPage = Number(siteSettings?.items_per_page) || DEFAULT_ITEMS_PER_PAGE;
  const visibleListings = listings.slice(0, visibleCount);
  const hasMore = visibleCount < listings.length;

  const loadMore = () => {
    setVisibleCount(prev => prev + itemsPerPage);
  };

  // Sidebar ad renderer
  const renderSidebarAd = (ad) => {
    if (!ad || !ad.active || !pageEnabled) return null;
    return (
      <div className="hidden lg:block w-40 xl:w-48 flex-shrink-0">
        <div className="sticky top-24 space-y-4">
          <div className="rounded-lg overflow-hidden border border-zinc-700">
            {ad.image_url ? (
              ad.link_url ? (
                <a href={ad.link_url} target="_blank" rel="noopener noreferrer">
                  <img src={ad.image_url} alt="Advertisement" className="w-full" />
                </a>
              ) : (
                <img src={ad.image_url} alt="Advertisement" className="w-full" />
              )
            ) : (
              <div className="h-[600px] bg-gradient-to-b from-zinc-800 via-zinc-700 to-zinc-800 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-zinc-400 text-sm">AD</p>
                  <p className="text-zinc-500 text-xs">160x600</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const seoData = siteSettings?.seo || {};
  const contentData = siteSettings?.content || {};

  return (
    <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
      <Helmet>
        <title>{seoData.default_title || contentData.site_title || ''}</title>
        <meta name="description" content={seoData.default_description || contentData.hero_description || ''} />
        <meta name="keywords" content={seoData.default_keywords || ''} />
        <meta property="og:title" content={seoData.default_title || contentData.site_title || ''} />
        <meta property="og:description" content={seoData.default_description || contentData.hero_description || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {seoData.default_og_image && <meta property="og:image" content={seoData.default_og_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={window.location.origin} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": contentData.site_title || "",
          "url": window.location.origin,
          "description": seoData.default_description || "",
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${window.location.origin}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}</script>
      </Helmet>

      {/* Top Banner Ad */}
      <AdBanner position="top" />

      {/* Main Content with Sidebar Ads */}
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar Ad */}
          {showSidebar && renderSidebarAd(leftAd)}

          {/* Freelancer Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              /* Skeleton Grid */
              <div className={gridClass}>
                {[...Array(8)].map((_, i) => (
                  <FreelancerCardSkeleton key={i} />
                ))}
              </div>
            ) : visibleListings.length > 0 ? (
              <>
                <div className={gridClass}>
                  {visibleListings.map((listing, index) => (
                    <FreelancerCard key={listing.id} freelancer={listing} index={index} />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="flex flex-col items-center mt-8 gap-2">
                    <p className="text-zinc-500 text-sm">
                      {visibleCount} / {listings.length} ilan gösteriliyor
                    </p>
                    <button
                      onClick={loadMore}
                      className="flex items-center gap-2 px-8 py-3 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white border border-zinc-700 hover:border-zinc-600 transition-all font-medium"
                    >
                      <ChevronDown className="w-5 h-5" />
                      Devamını Göster
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-24 animate-fade-in-up">
                <div className="w-24 h-24 bg-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-6 rotate-3">
                  <Search className="w-10 h-10 text-zinc-600" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">{t('common.noProfilesYet')}</h2>
                <p className="text-zinc-400 mb-8 max-w-md mx-auto leading-relaxed">
                  {t('common.beFirstToCreate')}
                </p>
                <Link to="/create-listing">
                  <Button className="bg-emerald-600 hover:bg-emerald-500 px-8 py-3 text-base transition-all hover:shadow-lg hover:shadow-emerald-500/20">
                    {t('common.createAListing')}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Right Sidebar Ad */}
          {showSidebar && renderSidebarAd(rightAd)}
        </div>
      </div>

      {/* SEO Content Section - Dynamic */}
      {homepageSections.filter(s => s.active).sort((a, b) => a.order - b.order).length > 0 && (
        <section className="border-t border-zinc-800 py-12 sm:py-16 bg-zinc-950">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {homepageSections.filter(s => s.active).sort((a, b) => a.order - b.order).map((section) => (
                <div key={section.id} className="mb-8 last:mb-0">
                  {section.type === 'text' ? (
                    <>
                      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">
                        {section.title}
                      </h2>
                      <div
                        className="text-zinc-300 text-center leading-relaxed mb-8"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                      />
                    </>
                  ) : section.type === 'cities' ? (
                    <>
                      <h3 className="text-xl font-semibold text-white mb-4">{section.title}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-8">
                        {(section.cities || []).map(city => (
                          <a
                            key={city}
                            href={`/search?city=${city}`}
                            className="text-zinc-400 hover:text-emerald-400 text-sm transition-all text-left hover:translate-x-0.5 inline-flex items-center gap-1.5 py-1"
                          >
                            <span className="w-1 h-1 bg-zinc-600 rounded-full group-hover:bg-emerald-500" />
                            {city}
                          </a>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
